import { BaseEntityDataSource } from "@brains/plugins";
import type {
  BaseQuery,
  PaginationInfo,
  BaseDataSourceContext,
  DataSourceSchema,
  EntityDataSourceConfig,
} from "@brains/plugins";
import { topicEntitySchema, type TopicEntity } from "../schemas/topic";
import type { Logger } from "@brains/utils/logger";
import { TOPIC_ENTITY_TYPE } from "../lib/constants";
import { toTopicDetail, toTopicSummary } from "../lib/topic-presenter";
import type { TopicDetailData } from "../templates/topic-detail/schema";
import type {
  TopicListData,
  TopicSummary,
} from "../templates/topic-list/schema";

/**
 * DataSource for fetching and transforming topic entities.
 * Handles both list and detail views for topics.
 */
export class TopicsDataSource extends BaseEntityDataSource<
  TopicEntity,
  TopicSummary,
  TopicListData
> {
  readonly id: string = "topics:entities";
  readonly name: string = "Topics Entity DataSource";
  readonly description: string =
    "Fetches and transforms topic entities for rendering";

  protected readonly config: EntityDataSourceConfig<TopicEntity> = {
    entityType: TOPIC_ENTITY_TYPE,
    entitySchema: topicEntitySchema,
    defaultSort: [{ field: "updated" as const, direction: "desc" as const }],
    defaultLimit: 100,
    lookupField: "id" as const,
  };

  constructor(logger: Logger) {
    super(logger);
    this.logger.debug("TopicsDataSource initialized");
  }

  protected transformEntity(entity: TopicEntity): TopicSummary {
    return toTopicSummary(entity);
  }

  protected buildListResult(
    items: TopicSummary[],
    _pagination: PaginationInfo | null,
    _query: BaseQuery,
  ): TopicListData {
    return {
      topics: items,
      totalCount: items.length,
    };
  }

  /**
   * Override fetch to handle detail view — detail needs full content.
   */
  override async fetch<T>(
    query: unknown,
    outputSchema: DataSourceSchema<T>,
    context: BaseDataSourceContext,
  ): Promise<T> {
    const { query: parsedQuery } = this.parseQuery(query);

    if (parsedQuery.id) {
      const entityService = context.entityService;
      const entity = await entityService.getEntity({
        entityType: this.config.entityType,
        id: parsedQuery.id,
      });

      if (!entity) {
        throw new Error(`Entity not found: ${parsedQuery.id}`);
      }

      return outputSchema.parse(
        toTopicDetail(entity) satisfies TopicDetailData,
      );
    }

    return super.fetch(query, outputSchema, context);
  }
}
